import { Head, router, useForm } from "@inertiajs/react"
import { Pin, Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import FileUpload from "@/components/file-upload"
import VideoPlayer from "@/components/players/video-player"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import { typedPatch } from "@/lib/typed-router"
import type { ToggleHighlightPin } from "@/types/router-mutations"
import type HighlightsIndexProps from "@/types/serializers/HighlightsIndex"
import type RequestsCreateHighlight from "@/types/serializers/RequestsCreateHighlight"

export default function HighlightsIndex({ highlights }: HighlightsIndexProps) {
  const [showForm, setShowForm] = useState(false)
  const [videoSignedId, setVideoSignedId] = useState<string | null>(null)

  const { data, setData, post, processing, errors, reset } =
    useForm<RequestsCreateHighlight>({
      highlight: {
        title: "",
        description: "",
        video_url: "",
        video: null,
        pinned: false,
      },
    })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/perfil/highlights", {
      onSuccess: () => {
        reset()
        setVideoSignedId(null)
        setShowForm(false)
      },
    })
  }

  function handleDelete(highlightId: number) {
    router.delete(`/perfil/highlights/${highlightId}`)
  }

  function handleTogglePin(
    highlight: HighlightsIndexProps["highlights"][number],
  ) {
    typedPatch<ToggleHighlightPin>(`/perfil/highlights/${highlight.id}`, {
      highlight: { pinned: !highlight.pinned },
    })
  }

  function handleVideoUploadComplete(signedId: string) {
    setVideoSignedId(signedId)
    setData("highlight", {
      ...data.highlight,
      video: signedId as unknown as File,
    })
  }

  return (
    <AppLayout>
      <Head title="Mis Highlights | KrownFT" />

      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Mis Highlights
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Subí videos de tus mejores jugadas
            </p>
          </div>
          <Button
            variant="accent"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => setShowForm(!showForm)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Agregar highlight
          </Button>
        </div>

        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="bg-card mb-8 rounded-lg border p-6 shadow-sm"
          >
            <h2 className="mb-4 text-lg font-semibold">Nuevo highlight</h2>
            <div className="grid gap-4">
              <div>
                <Label htmlFor="title">Título</Label>
                <Input
                  id="title"
                  value={data.highlight.title}
                  onChange={(e) =>
                    setData("highlight", {
                      ...data.highlight,
                      title: e.target.value,
                    })
                  }
                  placeholder="Ej: Gol vs Boca Juniors"
                />
                {errors["highlight.title"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["highlight.title"]}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="video_url">URL del video</Label>
                <Input
                  id="video_url"
                  value={data.highlight.video_url}
                  onChange={(e) =>
                    setData("highlight", {
                      ...data.highlight,
                      video_url: e.target.value,
                    })
                  }
                  placeholder="https://youtube.com/watch?v=..."
                />
                {errors["highlight.video_url"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["highlight.video_url"]}
                  </p>
                )}
              </div>
              <div>
                <Label>Archivo de video (opcional)</Label>
                <FileUpload
                  accept="video"
                  onUploadComplete={handleVideoUploadComplete}
                />
                {videoSignedId && (
                  <p className="mt-1 text-xs text-green-400">
                    Video subido correctamente
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="description">Descripción (opcional)</Label>
                <Textarea
                  id="description"
                  value={data.highlight.description}
                  onChange={(e) =>
                    setData("highlight", {
                      ...data.highlight,
                      description: e.target.value,
                    })
                  }
                  placeholder="Descripción del highlight..."
                  rows={3}
                />
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <Button type="submit" disabled={processing}>
                Guardar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                Cancelar
              </Button>
            </div>
          </form>
        )}

        {highlights.length === 0 ? (
          <p className="text-muted-foreground py-12 text-center">
            Aún no agregaste highlights
          </p>
        ) : (
          <div className="space-y-4">
            {highlights.map((highlight) => (
              <div
                key={highlight.id}
                className="bg-card overflow-hidden rounded-lg border shadow-sm"
              >
                {(highlight.stream_embed_url || highlight.video_url) && (
                  <VideoPlayer
                    streamEmbedUrl={highlight.stream_embed_url}
                    videoUrl={highlight.video_url}
                    streamStatus={highlight.stream_status}
                  />
                )}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{highlight.title}</h3>
                        {highlight.pinned && (
                          <span className="bg-krown-100 text-krown-700 rounded px-2 py-0.5 text-xs font-medium">
                            Fijado
                          </span>
                        )}
                      </div>
                      {highlight.description && (
                        <p className="text-muted-foreground mt-1 text-sm">
                          {highlight.description}
                        </p>
                      )}
                      <div className="text-muted-foreground mt-1 flex gap-3 text-xs">
                        {highlight.duration_seconds !== null && (
                          <span>
                            {Math.floor(highlight.duration_seconds / 60)}:
                            {String(highlight.duration_seconds % 60).padStart(
                              2,
                              "0",
                            )}
                          </span>
                        )}
                        <span>{highlight.views_count} vistas</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleTogglePin(highlight)}
                        aria-label={highlight.pinned ? "Desfijar" : "Fijar"}
                      >
                        <Pin
                          className={`h-4 w-4 ${highlight.pinned ? "text-krown-500" : "text-muted-foreground"}`}
                        />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(highlight.id)}
                        aria-label="Eliminar"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
